import { storage } from '../firebase/config';
import { ref, uploadBytes } from 'firebase/storage';
import imageCompression from 'browser-image-compression';

// Get bucket name dynamically like in admin portal
const bucketName = storage.app.options.storageBucket;

/**
 * Convert GIF to WebM using FFmpeg.wasm or similar
 * @param {File} gifFile - GIF file to convert
 * @returns {Promise<File>} WebM file
 */
export const convertGifToWebM = async (gifFile) => {
  try {
    // For now, we'll return the original GIF file
    // In production, you would use FFmpeg.wasm or a server-side conversion

    
    // TODO: Implement actual GIF to WebM conversion
    // This would require FFmpeg.wasm or server-side processing
    return gifFile;
  } catch (error) {
    console.error('Error converting GIF to WebM:', error);
    throw error;
  }
};

/**
 * Upload measurement instruction GIF/WebM with optimization
 * @param {File} file - GIF or WebM file to upload
 * @param {string} measurementType - Type of measurement (e.g., 'waist', 'chest', 'shirt', 'pant')
 * @param {Function} onProgress - Progress callback
 * @param {string} device - Device type (desktop/mobile)
 * @param {string} section - Section type (measurement-instructions or measurement-readymade)
 * @returns {Promise<Object>} Object containing URLs
 */
export const uploadMeasurementInstruction = async (file, measurementType, onProgress, device = 'desktop', section = 'measurement-instructions') => {
  try {
    const timestamp = Date.now();
    const originalName = `${measurementType}_instruction_${timestamp}`;
    
    // Check if file is GIF and convert to WebM if possible
    let processedFile = file;
    let fileExtension = 'gif';
    
    if (file.type === 'image/gif') {
      if (onProgress) onProgress(10, 'Converting GIF to WebM...');
      try {
        processedFile = await convertGifToWebM(file);
        fileExtension = 'webm';
      } catch (error) {
        console.warn('GIF to WebM conversion failed, using original GIF:', error);
        fileExtension = 'gif';
      }
    }
    
    if (onProgress) onProgress(20, 'Uploading measurement instruction...');
    
    // Upload the file - follow same structure as other website images
    const uploadDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    const storagePath = `vastre-images/${section}/${measurementType}/${device}/${uploadDate}`;
    const fileName = `${originalName}.${fileExtension}`;
    const filePath = `${storagePath}/${fileName}`;
    const fileRef = ref(storage, filePath);
    
    await uploadBytes(fileRef, processedFile);
    
    if (onProgress) onProgress(100, 'Upload complete!');
    
    return {
      original: {
        url: `https://${bucketName}/${filePath}`,
        path: filePath,
        size: processedFile.size,
        type: processedFile.type,
        measurementType: measurementType
      }
    };
  } catch (error) {
    console.error('Error uploading measurement instruction:', error);
    throw error;
  }
};

/**
 * Upload image and generate optimized versions
 * @param {File} file - Image file to upload
 * @param {string} path - Storage path (e.g., 'products', 'categories')
 * @param {string} filename - Custom filename
 * @returns {Promise<Object>} Object containing original and optimized URLs
 */
export const uploadOptimizedImage = async (file, storagePath, filename, onProgress) => {
  try {
    const timestamp = Date.now();
    const originalName = filename || `${timestamp}_${file.name}`;
    
    // Upload original image
    if (onProgress) onProgress(10, 'Uploading original image...');
    const originalPath = `${storagePath}/${originalName}`;
    const originalRef = ref(storage, originalPath);
    const originalSnapshot = await uploadBytes(originalRef, file);
    if (onProgress) onProgress(20, 'Original uploaded, starting optimization...');

    // Generate WebP versions with different sizes based on storage path
    let sizes;
    if (storagePath === 'reviews') {
      // For reviews: only 2 sizes - small and medium (much faster!)
      sizes = [400, 800];
    } else {
      // For products/categories: full resolution set
      sizes = [480, 768, 1080, 1440, 1920];
    }
    
    const optimizedURLs = {};
    
    for (let i = 0; i < sizes.length; i++) {
      const width = sizes[i];
      try {
        if (onProgress) {
          // For reviews: faster progress since fewer sizes
          const progress = storagePath === 'reviews' 
            ? 20 + ((i + 1) / sizes.length) * 70  // 20% to 90% (faster)
            : 20 + ((i + 1) / sizes.length) * 60; // 20% to 80% (normal)
          onProgress(progress, `Processing ${width}w version...`);
        }
        
        // Compress and convert to WebP
        const compressedFile = await imageCompression(file, {
          maxWidthOrHeight: width,
          useWebWorker: true,
          fileType: 'image/webp',
          maxSizeMB: storagePath === 'reviews' ? 1 : 2, // Lower limits for smaller files
          initialQuality: storagePath === 'reviews' ? 0.8 : 0.85, // Moderate quality for balance
          alwaysKeepResolution: true, // Preserve original resolution
          preserveExif: false, // Remove EXIF to reduce file size
          maxIteration: 10 // Faster compression
        });
        
        // Upload WebP version
        const webpFileName = `${originalName}_${width}w.webp`;
        const webpPath = `${storagePath}/${webpFileName}`;
        const webpRef = ref(storage, webpPath);
        
        await uploadBytes(webpRef, compressedFile);
        
        optimizedURLs[`${width}w`] = {
          width,
          url: `https://${bucketName}/${webpPath}`,
          path: webpPath,
          size: compressedFile.size,
          type: 'image/webp'
        };
      } catch (error) {
        console.warn(`Failed to process ${width}w version:`, error);
      }
    }
    
    if (onProgress) onProgress(90, 'Finalizing...');

    return {
      original: {
        url: `https://${bucketName}/${originalPath}`,
        path: originalPath,
        size: file.size,
        type: file.type
      },
      optimized: optimizedURLs
    };
  } catch (error) {
    console.error('Error uploading optimized image:', error);
    throw error;
  }
};
